import { useCallback, useEffect, useRef, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { OwnerPicker, useAiOwners, type AiOwner } from '@/pages/admin/OwnerPicker'

// AI-INCIDENT-LOG-1 — a record for when an AI feature misbehaves: what happened,
// what was done, who signed it off. A record only: it switches nothing off and
// changes nothing about how AI runs. Nothing is deleted; edit to correct.

interface Incident {
  id: string
  summary: string
  severity: string
  occurred_on: string
  feature_key: string
  feature_name: string | null
  action_taken: string
  status: string
  signed_off_by: string
  assigned_to: string
}

interface Report {
  incidents: Incident[]
  features: { key: string; name: string }[]
  summary?: { open: number; resolved: number; open_high: number }
}

const SEVERITIES = ['low', 'medium', 'high']
const BASE = '/v1/admin/ai-incidents'
const EMPTY_FORM = { summary: '', severity: '', occurred_on: '', feature_key: '', assigned_to: '' }

function errorText(err: unknown): string {
  const e = err as { response?: { data?: { detail?: unknown } }; message?: string }
  const detail = e?.response?.data?.detail
  return typeof detail === 'string' ? detail : e?.message || 'Save failed.'
}

function IncidentRow({ inc, owners, error, onSave }: {
  inc: Incident
  owners: AiOwner[]
  error?: string
  onSave: (id: string, body: Record<string, string>) => void
}) {
  const [status, setStatus] = useState(inc.status)
  const [action, setAction] = useState(inc.action_taken || '')
  const [signoff, setSignoff] = useState(inc.signed_off_by || '')
  const [assigned, setAssigned] = useState(inc.assigned_to || '')
  // Reset only when the saved values change, never on mount: a mount-time reset can
  // land after a value typed straight away and wipe it.
  const seen = useRef([inc.status, inc.action_taken || '', inc.signed_off_by || '', inc.assigned_to || ''])
  useEffect(() => {
    const next = [inc.status, inc.action_taken || '', inc.signed_off_by || '', inc.assigned_to || '']
    if (next.every((v, i) => v === seen.current[i])) return
    seen.current = next
    setStatus(next[0]); setAction(next[1]); setSignoff(next[2]); setAssigned(next[3])
  }, [inc.status, inc.action_taken, inc.signed_off_by, inc.assigned_to])
  return (
    <tr data-testid={`inc-row-${inc.id}`} className="border-t align-top">
      <td className="py-2 pr-2">{inc.occurred_on}</td>
      <td className="py-2 pr-2">{inc.feature_name ?? 'Not tied to one feature'}</td>
      <td className="py-2 pr-2">{inc.severity}</td>
      <td className="py-2 pr-2">{inc.summary}</td>
      <td className="py-2 pr-2">
        <select data-testid={`inc-status-${inc.id}`} className="rounded border px-2 py-1" value={status}
          onChange={e => setStatus(e.target.value)}>
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
        </select>
      </td>
      <td className="py-2 pr-2">
        <textarea data-testid={`inc-action-${inc.id}`} rows={2} maxLength={2000} placeholder="What was done"
          className="w-56 rounded border px-2 py-1" value={action} onChange={e => setAction(e.target.value)} />
      </td>
      <td className="py-2 pr-2">
        <OwnerPicker owners={owners} allowBlank testId={`inc-assigned-${inc.id}`} value={assigned}
          onChange={setAssigned} />
      </td>
      <td className="py-2 pr-2">
        <OwnerPicker owners={owners} allowBlank testId={`inc-signoff-${inc.id}`} value={signoff}
          onChange={setSignoff} />
        {error && <span data-testid={`inc-error-${inc.id}`} className="mt-1 block text-sm text-red-600">{error}</span>}
      </td>
      <td className="py-2">
        <Button type="button" size="sm" data-testid={`inc-save-${inc.id}`}
          onClick={() => onSave(inc.id, {
            status, action_taken: action.trim(), signed_off_by: signoff.trim(), assigned_to: assigned.trim(),
          })}>Save</Button>
      </td>
    </tr>
  )
}

export function AiIncidentsSection() {
  const { data, loading, error, execute } = useApi<Report>()
  const [report, setReport] = useState<Report | null>(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [formError, setFormError] = useState('')
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  const owners = useAiOwners()

  const load = useCallback(
    () => execute(() => apiClient.get<Report>(BASE).then(res => res.data)),
    [execute],
  )
  useEffect(() => { load() }, [load])
  useEffect(() => { if (data) setReport(data) }, [data])

  const set = (patch: Partial<typeof form>) => setForm(prev => ({ ...prev, ...patch }))
  const incidents = report?.incidents ?? []
  const summary = report?.summary

  const add = async () => {
    setFormError('')
    try {
      const res = await apiClient.post<Report>(BASE, {
        summary: form.summary.trim(), severity: form.severity, occurred_on: form.occurred_on,
        feature_key: form.feature_key, assigned_to: form.assigned_to.trim(),
      })
      setReport(res.data)
      setForm(EMPTY_FORM)
    } catch (err) {
      setFormError(errorText(err))
    }
  }

  const save = async (id: string, body: Record<string, string>) => {
    setRowErrors(prev => ({ ...prev, [id]: '' }))
    try {
      const res = await apiClient.put<Report>(`${BASE}/${id}`, body)
      setReport(res.data)
    } catch (err) {
      setRowErrors(prev => ({ ...prev, [id]: errorText(err) }))
    }
  }

  return (
    <section data-testid="ai-incidents-section" className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">Incidents</h2>
      {error && <div className="text-sm text-red-600">{error}</div>}
      {loading && !report && <p className="text-sm text-gray-500">Loading…</p>}

      {summary && (
        <p data-testid="inc-summary" className="text-sm text-gray-700">
          {summary.open} open - {summary.resolved} resolved - {summary.open_high} open high severity
        </p>
      )}

      <div className="flex flex-wrap items-start gap-2">
        <input type="text" data-testid="inc-summary-input" placeholder="What happened (max 1000 characters)"
          maxLength={1000} className="w-72 rounded border px-2 py-1" value={form.summary}
          onChange={e => set({ summary: e.target.value })} />
        <select data-testid="inc-severity" className="rounded border px-2 py-1" value={form.severity}
          onChange={e => set({ severity: e.target.value })}>
          <option value="">Choose severity</option>
          {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="date" data-testid="inc-date" className="rounded border px-2 py-1" value={form.occurred_on}
          onChange={e => set({ occurred_on: e.target.value })} />
        <select data-testid="inc-feature" className="rounded border px-2 py-1" value={form.feature_key}
          onChange={e => set({ feature_key: e.target.value })}>
          <option value="">Not tied to one feature</option>
          {(report?.features ?? []).map(f => <option key={f.key} value={f.key}>{f.name}</option>)}
        </select>
        <OwnerPicker owners={owners} allowBlank testId="inc-assigned" value={form.assigned_to}
          onChange={assigned_to => set({ assigned_to })} />
        <Button type="button" size="sm" data-testid="inc-add" onClick={add}>Add</Button>
      </div>
      <p className="text-xs text-gray-500">
        Use your own words. Do not paste customer data or AI prompts or responses into an incident.
      </p>
      {formError && <p data-testid="inc-error" className="text-sm text-red-600">{formError}</p>}

      {incidents.length === 0 ? (
        report && <p data-testid="inc-empty" className="text-sm text-gray-500">No incidents logged.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-600">
              <th>Date</th><th>Feature</th><th>Severity</th><th>What happened</th><th>Status</th>
              <th>Action taken</th><th>Assigned to</th><th>Signed off by</th><th />
            </tr>
          </thead>
          <tbody>
            {incidents.map(i => (
              <IncidentRow key={i.id} inc={i} owners={owners} error={rowErrors[i.id]} onSave={save} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
